import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEye,
    faEyeSlash,
    faChevronDown,
    faPaperPlane,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthContext";
import "./ClientPortal.css";

const AccessForm = () => {
    const { t } = useTranslation();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [state, setState] = useState("idle");
    const [error, setError] = useState("");

    const handleChange = (e) =>
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setState("submitting");
        setError("");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL || ""}/api/contact`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        name: form.name,
                        email: form.email,
                        service: "Portal Access Request",
                        message:
                            form.message || "Portal access request submitted.",
                    }),
                },
            );
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || "Submission failed.");
            }
            setState("success");
        } catch (err) {
            setState("error");
            setError(err.message);
        }
    };

    if (state === "success") {
        return (
            <div className="access-form__success">
                <FontAwesomeIcon icon={faCheckCircle} />
                <p>{t("portalPage.accessSuccess")}</p>
            </div>
        );
    }

    return (
        <form className="access-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
                <label htmlFor="acc-name">
                    {t("auth.name")} <span className="required">*</span>
                </label>
                <input
                    id="acc-name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder={t("portalPage.accessNamePlaceholder")}
                />
            </div>
            <div className="form-group">
                <label htmlFor="acc-email">
                    {t("auth.email")} <span className="required">*</span>
                </label>
                <input
                    id="acc-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder={t("portalPage.accessEmailPlaceholder")}
                />
            </div>
            <div className="form-group">
                <label htmlFor="acc-message">
                    {t("portalPage.accessContextLabel")}
                </label>
                <textarea
                    id="acc-message"
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    placeholder={t("portalPage.accessContextPlaceholder")}
                />
            </div>
            {state === "error" && <div className="error-message">{error}</div>}
            <button
                type="submit"
                className="btn btn--primary access-form__submit"
                disabled={state === "submitting"}
            >
                <FontAwesomeIcon icon={faPaperPlane} />
                {state === "submitting"
                    ? t("portalPage.accessSubmitting")
                    : t("portalPage.accessSubmit")}
            </button>
        </form>
    );
};

const ClientPortal = () => {
    const { t } = useTranslation();
    const { user, loading, login } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && user) {
            navigate(user.role === "admin" ? "/admin" : "/dashboard", {
                replace: true,
            });
        }
    }, [user, loading, navigate]);

    const [formData, setFormData] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [accessOpen, setAccessOpen] = useState(false);

    const handleChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setErrorMessage("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage("");
        try {
            const user = await login(formData);
            if (user.account_status === "pending") {
                navigate("/pending");
            } else if (user.account_status === "declined") {
                setErrorMessage(
                    "Your account application was not approved. Please contact support.",
                );
            } else if (user.role === "admin") {
                navigate("/admin");
            } else {
                navigate("/dashboard");
            }
        } catch (err) {
            setErrorMessage(err.message || t("common.error"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="portal-page">
            <div className="portal-page__inner">
                {/* ── Login Card ─────────────────────────────────── */}
                <div className="portal-page__card">
                    <h1 className="portal-page__title">
                        {t("auth.loginTitle")}
                    </h1>
                    <p className="portal-page__subtitle">
                        {t("portalPage.subtitle")}
                    </p>

                    {errorMessage && (
                        <div className="error-message">{errorMessage}</div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                        <div className="form-group">
                            <label htmlFor="email">{t("auth.email")}</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="you@company.com"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">
                                {t("auth.password")}
                            </label>
                            <div className="portal-page__password-wrap">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    className="portal-page__password-toggle"
                                    onClick={() => setShowPassword((v) => !v)}
                                    tabIndex={-1}
                                    aria-label="Toggle password visibility"
                                >
                                    <FontAwesomeIcon
                                        icon={showPassword ? faEyeSlash : faEye}
                                    />
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn--primary portal-page__submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? t("auth.loggingIn")
                                : t("auth.login")}
                        </button>
                    </form>
                </div>

                {/* ── Portal overview ────────────────────────────── */}
                <div className="portal-overview">
                    <p className="portal-overview__label">
                        {t("portalPage.overviewLabel")}
                    </p>
                    <ul className="portal-overview__list">
                        {t("portalPage.overviewItems", {
                            returnObjects: true,
                        }).map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </div>

                {/* ── Need Access ────────────────────────────────── */}
                <div className="portal-access">
                    <button
                        className="portal-access__toggle"
                        onClick={() => setAccessOpen((o) => !o)}
                        aria-expanded={accessOpen}
                    >
                        <span>{t("portalPage.accessTitle")}</span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`portal-access__chevron ${accessOpen ? "portal-access__chevron--open" : ""}`}
                        />
                    </button>

                    {accessOpen && (
                        <div className="portal-access__body">
                            <p className="portal-access__desc">
                                {t("portalPage.accessDesc")}
                            </p>
                            <AccessForm />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientPortal;
